import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toTitleCase } from "@/lib/text-utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import { buildAutoFilters } from "@/lib/auto-filters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  FileText,
  Bookmark,
  Loader2,
  Plus,
  LayoutTemplate,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import ReportDownloadView from "./ReportDownloadView";
import QueryPickerDialog from "./QueryPickerDialog";
import { TemplateBrowserDialog, TemplatePromptDialog } from "@/components/TemplateBrowserDialog";
import type { WorkflowTemplate } from "@/lib/template-constants";

interface ReportQuery {
  id: string;
  name: string;
  description: string | null;
  sql_query: string;
  database_connection_id: string | null;
  filter_config: any;
  bookmarked: boolean;
  updated_at: string;
  generation_mode: string;
}

const ReportsManager = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { schemaData } = useMcpSchema();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [nameFilter, setNameFilter] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  // Report template browser state
  const [reportTemplateBrowserOpen, setReportTemplateBrowserOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [templatePromptOpen, setTemplatePromptOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Selected report from URL or localStorage
  const reportParam = searchParams.get("report");
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    return reportParam || localStorage.getItem("last_report_query_id") || null;
  });

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["report-queries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_queries" as any)
        .select("id, name, description, sql_query, database_connection_id, filter_config, bookmarked, updated_at, generation_mode")
        .eq("is_report", true)
        .is("deleted_at", null)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return ((data || []) as any[]).map((q) => ({ ...q, bookmarked: !!q.bookmarked })) as ReportQuery[];
    },
  });

  // Auto-select first report if none selected
  useEffect(() => {
    if (!selectedId && reports.length > 0) {
      setSelectedId(reports[0].id);
    }
  }, [reports, selectedId]);

  // Persist selected report
  useEffect(() => {
    if (selectedId) {
      localStorage.setItem("last_report_query_id", selectedId);
    }
  }, [selectedId]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setSearchParams((prev) => { prev.set("report", id); return prev; });
  };

  const toggleBookmark = async (id: string) => {
    const r = reports.find((q) => q.id === id);
    if (!r) return;
    const newVal = !r.bookmarked;
    queryClient.setQueryData(["report-queries"], (old: ReportQuery[] | undefined) =>
      (old || []).map((q) => (q.id === id ? { ...q, bookmarked: newVal } : q)),
    );
    await supabase.from("saved_queries" as any).update({ bookmarked: newVal } as any).eq("id", id);
  };

  const handlePickerSelect = async (query: { id: string; name: string; sql_query: string }) => {
    await supabase.from("saved_queries" as any).update({ is_report: true } as any).eq("id", query.id);
    queryClient.invalidateQueries({ queryKey: ["report-queries"] });
    setSelectedId(query.id);
  };

  const handleGenerateReport = async (prompt: string) => {
    setGenerating(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      // Fetch schema context
      let schemaContext = "";
      try {
        const schemaRes = await fetch(`${supabaseUrl}/functions/v1/mcp-api/getSchema`, {
          method: "GET",
          headers,
        });
        if (schemaRes.ok) {
          const sd = await schemaRes.json();
          const tables = Array.isArray(sd) ? sd : sd?.tables || [];
          schemaContext = (Array.isArray(tables) ? tables : [])
            .map((t: any) => {
              const cols = (t.columns || []).map((c: any) => `  ${c.column_name || c.name} (${c.data_type || c.type})`).join("\n");
              return `Table: ${t.table_name || t.name}\n${cols}`;
            })
            .join("\n\n");
        }
      } catch { /* best-effort */ }

      // Generate dashboard via edge function
      const genRes = await fetch(`${supabaseUrl}/functions/v1/generate-dashboard`, {
        method: "POST",
        headers,
        body: JSON.stringify({ prompt, schemaContext }),
      });

      if (!genRes.ok) {
        const errData = await genRes.json().catch(() => ({ error: "Failed to generate report" }));
        throw new Error(errData.error || "Failed to generate report");
      }

      const raw = await genRes.json();
      const { _usage, ...generatedSpec } = raw;
      const widgets = generatedSpec.widgets || [];
      widgets.forEach((w: any) => {
        if (w.sqlQuery?.trim()) {
          w._queryName = w.title || "Report Query";
        }
      });

      const autoFilters = buildAutoFilters(widgets, schemaData);
      const spec = {
        widgets,
        description: selectedTemplate?.description || "",
        ...(autoFilters.length > 0 ? { configuredFilters: autoFilters } : {}),
      };

      const name = selectedTemplate?.name ?? "New Report";
      const { data, error } = await supabase
        .from("dashboard_snapshots")
        .insert({
          title: name,
          spec: spec as any,
          client_id: profile?.client_id ?? "",
          profile_id: profile?.id,
        } as any)
        .select("id")
        .single();

      if (error) throw error;

      toast({ title: "Created", description: "Report dashboard generated! Redirecting..." });
      // Navigate to dashboards page with the new dashboard selected
      navigate(`/dashboards?id=${data.id}`);
    } catch (err: any) {
      console.error("Generate report error:", err);
      toast({ title: "Error", description: err.message || "Failed to generate report", variant: "destructive" });
    } finally {
      setGenerating(false);
      setSelectedTemplate(null);
    }
  };

  const filtered = reports.filter((r) =>
    r.name.toLowerCase().includes(nameFilter.toLowerCase()),
  );

  const selectedReport = reports.find((r) => r.id === selectedId);

  return (
    <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <div className="w-[272px] shrink-0 border-r border-border flex flex-col bg-card">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Reports</span>
          <span className="ml-auto text-xs text-muted-foreground">{reports.length}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-1"
            onClick={() => setPickerOpen(true)}
            title="Add Report"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setReportTemplateBrowserOpen(true)}
            title="Report Templates"
          >
            <LayoutTemplate className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search reports…"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 gap-2">
              <FileText className="h-8 w-8 text-muted-foreground" />
              <p className="text-xs text-muted-foreground text-center">
                {reports.length === 0
                  ? 'No reports yet. Click "+" to add a query as a report.'
                  : "No reports match your search."}
              </p>
            </div>
          ) : (
            filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => handleSelect(r.id)}
                className={cn(
                  "w-full text-left px-3 py-2.5 border-b border-border transition-colors hover:bg-accent/50",
                  selectedId === r.id && "bg-accent",
                )}
              >
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{toTitleCase(r.name)}</p>
                    {r.description && (
                      <p className="text-[11px] text-muted-foreground truncate">{r.description}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {format(new Date(r.updated_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={(e) => { e.stopPropagation(); toggleBookmark(r.id); }}
                  >
                    <Bookmark className={cn("h-3.5 w-3.5", r.bookmarked ? "fill-primary text-primary" : "text-muted-foreground")} />
                  </Button>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Main panel */}
      <div className="flex flex-1 min-h-0">
        {selectedReport ? (
          <ReportDownloadView reportId={selectedReport.id} />
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-muted-foreground">
                {reports.length === 0
                  ? 'Click "+" to add a query as a report.'
                  : "Select a report from the sidebar"}
              </p>
              {reports.length === 0 && (
                <Button
                  variant="outline"
                  onClick={() => setReportTemplateBrowserOpen(true)}
                  className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                >
                  <LayoutTemplate className="h-4 w-4" />
                  Report Templates
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <QueryPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handlePickerSelect}
      />

      <TemplateBrowserDialog
        open={reportTemplateBrowserOpen}
        onClose={() => setReportTemplateBrowserOpen(false)}
        templateType="report"
        title="Choose a Report Template"
        description="Browse pre-built report templates with scheduled delivery"
        onSelectTemplate={(t) => {
          setReportTemplateBrowserOpen(false);
          setSelectedTemplate(t);
          setTemplatePromptOpen(true);
        }}
      />

      <TemplatePromptDialog
        open={templatePromptOpen}
        onClose={() => { setTemplatePromptOpen(false); setSelectedTemplate(null); }}
        template={selectedTemplate}
        generateLabel={generating ? "Generating..." : "Generate Report"}
        placeholder="Describe the report you want to generate..."
        onGenerate={async (prompt) => {
          setTemplatePromptOpen(false);
          await handleGenerateReport(prompt);
        }}
      />
    </div>
  );
};

export default ReportsManager;
